<template>
  <div class="lease-file-upload-page" data-theme="v1">
    <!-- Main Content -->
    <div class="lease-file-upload-page__container">
      <!-- Header Section -->
      <div class="lease-file-upload-page__page-header">
        <div class="lease-file-upload-page__header-content">
          <div class="lease-file-upload-page__header-icon">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
            </svg>
          </div>
          <div>
            <h2 class="lease-file-upload-page__page-title">Lease AI Document Processor</h2>
            <p class="lease-file-upload-page__page-description">Upload lease documents for AI-powered analysis and processing</p>
          </div>
        </div>
      </div>

      <!-- File Upload Component -->
      <div class="lease-file-upload-page__upload-section">
        <!-- File Drop Zone -->
        <div 
          class="lease-file-upload-page__dropzone"
          :class="{ 
            'lease-file-upload-page__dropzone--dragover': isDragOver, 
            'lease-file-upload-page__dropzone--uploading': uploading 
          }"
          @drop="handleDrop"
          @dragover="handleDragOver"
          @dragenter="handleDragEnter"
          @dragleave="handleDragLeave"
          @click="triggerFileInput"
        >
          <input 
            ref="fileInput"
            type="file"
            class="lease-file-upload-page__file-input"
            accept=".pdf"
            @change="handleFileSelect"
          >
          
          <div class="lease-file-upload-page__dropzone-content">
            <div v-if="!uploading" class="lease-file-upload-page__upload-icon">
              <inline-svg name="upload" classes="w-12 h-12 fill-current"></inline-svg>
            </div>
            <div v-else class="lease-file-upload-page__upload-icon">
              <inline-svg name="loading-button" classes="w-12 h-12 fill-current animate-spin"></inline-svg>
            </div>
            
            <div class="lease-file-upload-page__upload-text">
              <h3 v-if="!uploading" class="lease-file-upload-page__dropzone-title">
                Drop your legal documents here
              </h3>
              <h3 v-else class="lease-file-upload-page__dropzone-title lease-file-upload-page__dropzone-title--uploading">
                Processing file...
              </h3>
              
              <p v-if="!uploading" class="lease-file-upload-page__dropzone-subtitle">
                or <span class="lease-file-upload-page__browse-link">click to browse</span>
              </p>
              <p v-else class="lease-file-upload-page__dropzone-subtitle">
                Please wait while we process your document
              </p>
            </div>
            
            <div class="lease-file-upload-page__file-info">
              Supported formats: PDF only (Max 100MB)
            </div>
          </div>
        </div>

        <!-- Progress Bar -->
        <div v-if="showProgress && (jobStatus === 'queued' || jobStatus === 'processing')" class="lease-file-upload-page__progress">
          <div class="lease-file-upload-page__progress-header">
            <span class="lease-file-upload-page__progress-label">{{ currentFileName }}</span>
            <span class="lease-file-upload-page__progress-status">{{ getStatusLabel(jobStatus) }}</span>
          </div>
          <div class="lease-file-upload-page__progress-bar">
            <div 
              class="lease-file-upload-page__progress-fill"
              :class="{ 'lease-file-upload-page__progress-fill--animated': jobStatus === 'processing' }"
              :style="{ width: progressPercentage + '%' }"
            ></div>
          </div>
          <div class="lease-file-upload-page__progress-percentage">{{ progressPercentage }}%</div>
        </div>

        <!-- Error Messages -->
        <div v-if="uploadError" class="lease-file-upload-page__message lease-file-upload-page__message--error">
          <div class="lease-file-upload-page__message-icon">
            <svg class="w-5 h-5 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L1 21h22L12 2zm0 3.99L19.53 19H4.47L12 5.99zM11 16v2h2v-2h-2zm0-6v4h2v-4h-2z"/>
            </svg>
          </div>
          <div class="lease-file-upload-page__message-content">
            <p>{{ uploadError }}</p>
          </div>
        </div>

        <!-- Success Message -->
        <div v-if="uploadSuccess" class="lease-file-upload-page__message lease-file-upload-page__message--success">
          <div class="lease-file-upload-page__message-icon">
            <inline-svg name="check-circle" classes="w-5 h-5 fill-current"></inline-svg>
          </div>
          <div class="lease-file-upload-page__message-content">
            <p>File uploaded successfully! Analysis in progress.</p>
          </div>
        </div>
      </div>

      <!-- Completed Files Table -->
      <div v-if="completedJobs.length > 0" class="lease-file-upload-page__table-section">
        <h3 class="lease-file-upload-page__table-title">Completed Analyses</h3>
        
        <themed-table
          ref="filesTable"
          :data="completedJobs"
          :columns="tableColumns"
          :searchable="false"
          :sortable="false"
          :pagination="paginationConfig"
          :per-page-options="[]"
          :striped="false"
          :hoverable="true"
          :loading="false"
          aria-label="Completed Lease Analyses"
          @action="handleAction"
        >
          <template #column-status="{ row }">
            <span :class="['status-text', `status-text--${row.status}`]">
              {{ getStatusLabel(row.status).toUpperCase() }}
            </span>
          </template>
          
          <template #actions="{ row }">
            <span 
              v-if="row.status === 'done'"
              class="status-badge status-view"
              @click="handleAction({ action: 'view', row })"
              role="button"
              tabindex="0"
              @keydown.enter="handleAction({ action: 'view', row })"
              @keydown.space.prevent="handleAction({ action: 'view', row })"
            >
              View
            </span>
          </template>
        </themed-table>
      </div>
    </div>

    <!-- Email Modal -->
    <email-modal
      :show.sync="showEmailModal"
      title="Enter Your Email"
      description="Please provide your email address to access the lease analysis results."
      calculation="lease_audit"
      @submit="handleEmailSubmit"
      @cancel="handleEmailCancel"
    />
  </div>
</template>

<script>
import moment from 'moment';
import EmailModal from '../../../common/EmailModal.vue';

export default {
  name: 'LeaseFileUploadPage',

  components: {
    EmailModal,
  },

  data() {
    return {
      isDragOver: false,
      uploading: false,
      uploadSuccess: false,
      uploadError: null,
      currentJobId: null,
      currentFileName: null,
      jobStatus: null,
      completedJobs: [],
      statusCheckInterval: null,
      showProgress: false,
      showEmailModal: false,
      pendingJobToView: null,
      userEmail: null,
      statusCheckCount: 0, // Track number of API calls made
      estimatedTotalChecks: 17, // Average number of checks before completion
      tableColumns: [
        {
          field: 'input_file',
          label: 'File Name',
          type: 'text',
          sortable: false,
          searchable: true
        },
        {
          field: 'status',
          label: 'Status',
          type: 'text',
          align: 'center',
          sortable: false,
          searchable: true
        },
        {
          type: 'actions',
          label: 'Actions',
          width: '120px',
          align: 'center',
          buttons: [
            {
              name: 'view',
              label: 'View',
              variant: 'primary',
              size: 'sm',
              visible: (row) => row.status === 'done'
            }
          ]
        }
      ]
    };
  },

  computed: {
    progressPercentage() {
      if (this.jobStatus === 'done') return 100;
      
      // Calculate progress based on number of API calls made
      // Start at 10%, increment with each check, cap at 95% until done
      const baseProgress = 10;
      const maxProgress = 95;
      const progressPerCheck = (maxProgress - baseProgress) / this.estimatedTotalChecks;
      
      const calculatedProgress = baseProgress + (this.statusCheckCount * progressPerCheck);
      
      // Cap at 95% until we get 'done' status
      return Math.min(Math.round(calculatedProgress), maxProgress);
    },

    paginationConfig() {
      return {
        enabled: true,
        perPage: 10,
        currentPage: 1,
        total: this.completedJobs.length
      };
    }
  },

  mounted() {
    // Load completed jobs from localStorage
    this.loadCompletedJobs();
    
    // Check if user info is already stored in session
    const storedEmail = sessionStorage.getItem('public_view_email');
    const storedName = sessionStorage.getItem('public_view_name');
    const storedPhone = sessionStorage.getItem('public_view_phone');
    
    if (storedEmail) {
      this.userEmail = storedEmail;
      // Update the header component with the user info
      if (this.$refs.header) {
        this.$refs.header.userEmail = storedEmail;
        if (storedName) {
          this.$refs.header.userName = storedName;
        }
        if (storedPhone) {
          this.$refs.header.userPhone = storedPhone;
        }
      }
    }
  },

  beforeDestroy() {
    // Clear interval when component is destroyed
    if (this.statusCheckInterval) {
      clearInterval(this.statusCheckInterval);
    }
  },

  methods: {
    handleDragOver(e) {
      e.preventDefault();
      this.isDragOver = true;
    },

    handleDragEnter(e) {
      e.preventDefault();
      this.isDragOver = true;
    },

    handleDragLeave(e) {
      e.preventDefault();
      // Only set to false if we're leaving the dropzone container
      if (!e.currentTarget.contains(e.relatedTarget)) {
        this.isDragOver = false;
      }
    },

    handleDrop(e) {
      e.preventDefault();
      this.isDragOver = false;
      
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        this.processFile(files[0]);
      }
    },

    triggerFileInput() {
      if (!this.uploading) {
        this.$refs.fileInput.click();
      }
    },

    handleFileSelect(e) {
      const files = Array.from(e.target.files);
      if (files.length > 0) {
        this.processFile(files[0]);
      }
    },

    async processFile(file) {
      // Clear previous messages
      this.uploadError = null;
      this.uploadSuccess = false;

      // Validate file
      if (!this.validateFile(file)) {
        return;
      }

      this.uploading = true;
      this.currentFileName = file.name;

      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch('/api/public/lease-audit/upload', {
          method: 'POST',
          body: formData,
          headers: {
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
          }
        });

        const data = await response.json();

        if (response.ok && data.success) {
          this.uploadSuccess = true;
          
          // Store job ID and start polling
          if (data.api_response && data.api_response.job_id) {
            this.currentJobId = data.api_response.job_id;
            this.jobStatus = 'queued';
            this.showProgress = true;
            this.startStatusPolling();
          }
          
          // Reset file input
          if (this.$refs.fileInput) {
            this.$refs.fileInput.value = '';
          }

          // Clear success message after 3 seconds
          setTimeout(() => {
            this.uploadSuccess = false;
          }, 3000);
        } else {
          this.uploadError = data.message || 'Upload failed. Please try again.';
        }
      } catch (error) {
        console.error('Upload error:', error);
        this.uploadError = 'An error occurred. Please try again.';
      } finally {
        this.uploading = false;
      }
    },

    validateFile(file) {
      const maxSize = 100 * 1024 * 1024; // 100MB

      if (file.size > maxSize) {
        this.uploadError = 'File size must be less than 100MB';
        return false;
      }

      if (file.type !== 'application/pdf') {
        this.uploadError = 'Only PDF files are allowed';
        return false;
      }

      return true;
    },

    startStatusPolling() {
      // Reset status check counter
      this.statusCheckCount = 0;
      
      // Check immediately
      this.checkJobStatus();
      
      // Then poll every 10 seconds
      this.statusCheckInterval = setInterval(async () => {
        await this.checkJobStatus();
      }, 10000); // Changed from 3000ms (3s) to 10000ms (10s)
    },

    async checkJobStatus() {
      if (!this.currentJobId) return;

      try {
        // Increment the status check counter
        this.statusCheckCount++;
        
        const response = await fetch(`/api/public/lease-audit/status/${this.currentJobId}`, {
          headers: {
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
          }
        });

        const data = await response.json();

        if (response.ok && data.success) {
          const previousStatus = this.jobStatus;
          this.jobStatus = data.job.status;

          console.log('Job status:', this.jobStatus, 'Previous:', previousStatus, 'Check count:', this.statusCheckCount);

          // If job is completed or failed, stop polling
          if (data.job.status === 'done' || data.job.status === 'failed') {
            // Stop polling
            if (this.statusCheckInterval) {
              clearInterval(this.statusCheckInterval);
              this.statusCheckInterval = null;
            }

            // If completed successfully, add to table
            if (data.job.status === 'done') {
              // Add filename to job data if not present
              if (!data.job.input_file && this.currentFileName) {
                data.job.input_file = this.currentFileName;
              }
              
              this.addCompletedJob(data.job);
            }

            // Hide progress bar after a short delay
            setTimeout(() => {
              this.showProgress = false;
              this.currentJobId = null;
              this.currentFileName = null;
              this.jobStatus = null;
              this.statusCheckCount = 0; // Reset counter
            }, 2000);
          }
        }
      } catch (error) {
        console.error('Status check error:', error);
      }
    },

    addCompletedJob(job) {
      // Check if job already exists in the list
      const existingIndex = this.completedJobs.findIndex(j => j.job_id === job.job_id);
      
      if (existingIndex === -1) {
        // Add to beginning of array
        this.completedJobs.unshift(job);
        
        // Save to localStorage
        this.saveCompletedJobs();
        
        console.log('Job added to completed list:', job.job_id);
      }
    },

    loadCompletedJobs() {
      const stored = localStorage.getItem('public_lease_audit_jobs');
      if (stored) {
        try {
          this.completedJobs = JSON.parse(stored);
          console.log('Loaded completed jobs:', this.completedJobs.length);
        } catch (e) {
          console.error('Error loading completed jobs:', e);
          this.completedJobs = [];
        }
      }
    },

    saveCompletedJobs() {
      // Keep only last 10 jobs
      const jobsToSave = this.completedJobs.slice(0, 10);
      localStorage.setItem('public_lease_audit_jobs', JSON.stringify(jobsToSave));
      console.log('Saved completed jobs to localStorage:', jobsToSave.length);
    },

    handleAction({ action, row }) {
      if (action === 'view') {
        this.openEmailModal(row);
      }
    },

    openEmailModal(job) {
      // Check if user is authenticated
      const isAuthenticated = window.user && window.user.id;
      
      // If authenticated, navigate directly without email modal
      if (isAuthenticated) {
        window.location.href = `/public-lease-audit/${job.job_id}`;
        return;
      }
      
      // Check if email is already stored in sessionStorage
      const storedEmail = sessionStorage.getItem('public_view_email');
      
      if (storedEmail) {
        // Email already exists, navigate directly to details page
        window.location.href = `/public-lease-audit/${job.job_id}?email=${encodeURIComponent(storedEmail)}`;
      } else {
        // No email stored, show modal
        this.pendingJobToView = job;
        this.showEmailModal = true;
      }
    },

    handleEmailSubmit(userInfo) {
      // Store user info in session storage
      this.userEmail = userInfo.email;
      
      // Update the header component with the user info
      if (this.$refs.header) {
        this.$refs.header.userEmail = userInfo.email;
        this.$refs.header.userName = userInfo.fullName;
        this.$refs.header.userPhone = userInfo.phone;
      }
      
      // User info has been submitted and saved to database
      // Navigate to the details page
      if (this.pendingJobToView) {
        window.location.href = `/public-lease-audit/${this.pendingJobToView.job_id}?email=${encodeURIComponent(userInfo.email)}&name=${encodeURIComponent(userInfo.fullName)}&phone=${encodeURIComponent(userInfo.phone)}`;
      }
    },

    handleEmailCancel() {
      this.showEmailModal = false;
      this.pendingJobToView = null;
    },

    viewJob(job) {
      // This method is no longer directly called, but kept for reference
      // Navigate to public lease audit details page
      window.location.href = `/public-lease-audit/${job.job_id}`;
    },

    getStatusLabel(status) {
      const labels = {
        'queued': 'Queued',
        'processing': 'Processing',
        'done': 'Completed',
        'failed': 'Failed'
      };
      return labels[status] || status;
    },

    getStatusClass(status) {
      return `status-badge--${status}`;
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return moment(dateString).format('MMM D, YYYY h:mm A');
    }
  }
};
</script>
